/** Arcane Spire (TOWERS_V2): a beam that holds one target and hits harder the longer it stays on it. */
import { TowerArchetype } from "../../data/balance";
import { Fix64 } from "../../maths/fix64";
import { distanceSquared } from "../../world/roadGeometry";
import { Enemy, Tower } from "../entities";
import { SimEventKind } from "../events";
import { LevelSim } from "../levelSim";
import { mythicValue } from "../mythics";
import { applyHit, findTarget, findTargetExcept } from "./combat";
import { BASE_TIERS, branchOf, skill } from "../branches/ascension";
import { controllable, polymorph } from "../branches/statuses";

/**
 * One tick of a spire: it keeps its target while that enemy lives, stays in range and can be hit (ramp +1 tick, up to
 * `rampTicks`); otherwise it locks onto the best target by its priority with the ramp back at 0 (BeamStarted), or lets
 * go (BeamStopped). It pulses every fire interval for its damage × the ramp multiplier (`beamMultiplier`).
 */
export function tickBeam(sim: LevelSim, t: Tower, a: TowerArchetype): void {
  let target = t.beamTarget !== 0 ? sim.findEnemy(t.beamTarget) : null;
  if (target !== null && !holds(sim, t, a, target)) target = null;
  if (target === null) {
    const next = findTarget(sim, t);
    if (next === null) stopBeam(sim, t);
    else {
      t.beamTarget = next.id; t.beamTicks = 0;
      sim.events.add(SimEventKind.BeamStarted, t.id, next.id);
    }
    target = next;
  } else if (t.beamTicks < rampTicksOf(sim, t, a)) t.beamTicks++;
  if (t.tier > BASE_TIERS) holdExtraBeams(sim, t, a, target);   // tier 4: the Sorcerer's split beams
  if (t.cooldown > 0) { t.cooldown--; return; }
  if (target === null) return;
  t.cooldown = sim.fireIntervalOf(t);
  sim.events.add(SimEventKind.TowerFired, t.id, target.id);
  const damage = sim.damageOf(t).mul(beamMultiplier(sim, t));
  applyHit(sim, target, t, a, damage);
  if (t.tier <= BASE_TIERS) return;
  for (const id of t.extraBeams) { const e = id !== 0 ? sim.findEnemy(id) : null; if (e !== null) applyHit(sim, e, t, a, damage); }
  if (target.alive) maybePolymorph(sim, t, target);
}

/** The Sorcerer's extra beams (beams − 1, + Prism): each holds its own other target while it can, else finds the best free one. */
function holdExtraBeams(sim: LevelSim, t: Tower, a: TowerArchetype, main: Enemy | null): void {
  const b = branchOf(sim, t);
  const want = b === null || main === null ? 0 : Math.max(0, b.tier4.int("beams", 1) - 1 + (skill(sim, t, "prism")?.params.int("beams") ?? 0));
  t.extraBeams.length = Math.min(t.extraBeams.length, want);
  while (t.extraBeams.length < want) t.extraBeams.push(0);
  for (let i = 0; i < want; i++) {
    const held = t.extraBeams[i] !== 0 ? sim.findEnemy(t.extraBeams[i]) : null;
    if (held !== null && held.id !== main!.id && holds(sim, t, a, held)) continue;
    const taken = [main!.id, ...t.extraBeams.filter((id, j) => j !== i && id !== 0)];
    t.extraBeams[i] = findTargetExcept(sim, t, ...taken)?.id ?? 0;
  }
}

/** Polymorph: on a pulse once its clock has run, the beam's target (not a boss) becomes a sheep for a while. */
function maybePolymorph(sim: LevelSim, t: Tower, target: Enemy): void {
  const s = skill(sim, t, "polymorph");
  if (s === null || t.skillTimer > 0 || !controllable(target)) return;
  polymorph(sim, target, t, s.params.int("sheepTicks"));
  t.skillTimer = s.params.int("cooldownTicks");
  sim.events.add(SimEventKind.SkillFired, t.id, s.index, target.id);
}

/** The ramp's length: the Disintegrator's own at tier 4, else the archetype's. */
function rampTicksOf(sim: LevelSim, t: Tower, a: TowerArchetype): number {
  const b = t.tier > BASE_TIERS ? branchOf(sim, t) : null;
  return b !== null && b.tier4.has("rampTicks") ? b.tier4.int("rampTicks") : a.rampTicks;
}

/** The beam lets go (no target, or the spire was disabled): BeamStopped once, ramp to 0. */
export function stopBeam(sim: LevelSim, t: Tower): void {
  t.extraBeams.length = 0;
  if (t.beamTarget === 0 && t.beamTicks === 0) return;
  sim.events.add(SimEventKind.BeamStopped, t.id, t.beamTarget);
  t.beamTarget = 0; t.beamTicks = 0;
}

/** The ramp, 0 (just locked on) to 1 (full): ticks held over `rampTicks`. Zero for a tower that has no beam. */
export function beamRamp(sim: LevelSim, t: Tower): Fix64 {
  const a = sim.balance.towers[t.archetype];
  if (a.rampTicks <= 0 || t.beamTarget === 0) return Fix64.Zero;
  const ticks = rampTicksOf(sim, t, a);
  return Fix64.fromInt(Math.min(t.beamTicks, ticks)).divInt(ticks);
}

/** Damage multiplier now: 1 + (rampMax − 1 + Overcharge) × ramp. */
export function beamMultiplier(sim: LevelSim, t: Tower): Fix64 { return Fix64.One.add(beamTop(sim, t).mul(beamRamp(sim, t))); }

/** The multiplier at full ramp, 1 + (rampMax − 1 + Overcharge): a beam held on one long-lived target (M40 sim/wyrm.ts). */
export function beamFullMultiplier(sim: LevelSim, t: Tower): Fix64 { return Fix64.One.add(beamTop(sim, t)); }

function beamTop(sim: LevelSim, t: Tower): Fix64 {
  const a = sim.balance.towers[t.archetype];
  const rampMax = t.tier > BASE_TIERS ? branchOf(sim, t)?.tier4.num("rampMax", a.rampMax) ?? a.rampMax : a.rampMax;
  return rampMax.sub(Fix64.One).add(mythicValue(sim, t.archetype, sim.mythics.arcaneOvercharge));
}

function holds(sim: LevelSim, t: Tower, a: TowerArchetype, e: Enemy): boolean {
  if (!(e.isAir ? a.targetsAir : a.targetsGround) || !sim.targetable(e)) return false;
  const r = sim.rangeOf(t);
  return distanceSquared(Fix64.fromInt(t.cell.x), Fix64.fromInt(t.cell.y), e.x, e.y).le(r.mul(r));
}
